import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  medicationSheets: [],
  currentMedicationSheet: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Medication Sheets (optionally by patient ID)
export const fetchMedicationSheets = createAsyncThunk(
  'medicationSheets/fetchMedicationSheets',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/medication-sheets/patient/${patientId}`
        : '/medication-sheets';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch medication sheets'
      );
    }
  }
);

// Fetch a single Medication Sheet by ID
export const fetchMedicationSheet = createAsyncThunk(
  'medicationSheets/fetchMedicationSheet',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/medication-sheets/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch medication sheet'
      );
    }
  }
);

// Create a new Medication Sheet
export const createMedicationSheet = createAsyncThunk(
  'medicationSheets/createMedicationSheet',
  async (medicationData, { rejectWithValue }) => {
    try {
      const response = await api.post('/medication-sheets', medicationData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create medication sheet'
      );
    }
  }
);

// Update a Medication Sheet
export const updateMedicationSheet = createAsyncThunk(
  'medicationSheets/updateMedicationSheet',
  async ({ case_id, medicationData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/medication-sheets/case/${case_id}`, medicationData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update medication sheet'
      );
    }
  }
);

// Delete a Medication Sheet
export const deleteMedicationSheet = createAsyncThunk(
  'medicationSheets/deleteMedicationSheet',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/medication-sheets/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete medication sheet'
      );
    }
  }
);

// ==================== Slice ====================
const medicationSheetsSlice = createSlice({
  name: 'medicationSheets',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentMedicationSheet: (state, action) => {
      state.currentMedicationSheet = action.payload;
    },
    clearCurrentMedicationSheet: (state) => {
      state.currentMedicationSheet = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchMedicationSheets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMedicationSheets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.medicationSheets = action.payload;
      })
      .addCase(fetchMedicationSheets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchMedicationSheet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMedicationSheet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentMedicationSheet = action.payload;
      })
      .addCase(fetchMedicationSheet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createMedicationSheet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createMedicationSheet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.medicationSheets.unshift(action.payload);
      })
      .addCase(createMedicationSheet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateMedicationSheet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMedicationSheet.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.medicationSheets.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.medicationSheets[index] = updated;
        }

        if (state.currentMedicationSheet?.id === updated.id) {
          state.currentMedicationSheet = updated;
        }
      })
      .addCase(updateMedicationSheet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteMedicationSheet.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteMedicationSheet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.medicationSheets = state.medicationSheets.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentMedicationSheet?.id === action.payload) {
          state.currentMedicationSheet = null;
        }
      })
      .addCase(deleteMedicationSheet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentMedicationSheet,
  clearCurrentMedicationSheet,
} = medicationSheetsSlice.actions;

export default medicationSheetsSlice.reducer;